import React, { useState, FormEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hook";
import { runRecon, clearReconResult, Discrepancy } from "../../../features/recon/ReconSlice";
import { RootState } from "../../../store/store";
import { Loader2, AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Download } from "lucide-react";
import { downloadReconReport } from "../downloadReconReport";

// ── Discrepancy type config ───────────────────────────────────────────────────

const DISCREPANCY_META: Record<string, { label: string; badgeClass: string; chipClass: string }> = {
  MISSING_IN_SYSTEM:      { label: "Missing in System",    badgeClass: "bg-red-100 text-red-700 border border-red-200",          chipClass: "bg-red-50 border-red-300 text-red-700 hover:bg-red-100" },
  MISSING_IN_PROCESSOR:   { label: "Missing in SELP",      badgeClass: "bg-red-100 text-red-700 border border-red-200",          chipClass: "bg-red-50 border-red-300 text-red-700 hover:bg-red-100" },
  DUPLICATE_IN_SYSTEM:    { label: "Duplicate in System",  badgeClass: "bg-red-100 text-red-700 border border-red-200",          chipClass: "bg-red-50 border-red-300 text-red-700 hover:bg-red-100" },
  DUPLICATE_IN_PROCESSOR: { label: "Duplicate in SELP",    badgeClass: "bg-red-100 text-red-700 border border-red-200",          chipClass: "bg-red-50 border-red-300 text-red-700 hover:bg-red-100" },
  NULL_CODE_IN_SYSTEM:    { label: "No Code in System",    badgeClass: "bg-red-100 text-red-700 border border-red-200",          chipClass: "bg-red-50 border-red-300 text-red-700 hover:bg-red-100" },
  AMOUNT_MISMATCH:        { label: "Amount Mismatch",      badgeClass: "bg-orange-100 text-orange-700 border border-orange-200", chipClass: "bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100" },
  REFERENCE_MISMATCH:     { label: "Reference Mismatch",   badgeClass: "bg-orange-100 text-orange-700 border border-orange-200", chipClass: "bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100" },
  DATE_MISMATCH:          { label: "Date Mismatch",        badgeClass: "bg-yellow-100 text-yellow-700 border border-yellow-200", chipClass: "bg-yellow-50 border-yellow-300 text-yellow-700 hover:bg-yellow-100" },
  CURRENCY_MISMATCH:      { label: "Currency Mismatch",    badgeClass: "bg-yellow-100 text-yellow-700 border border-yellow-200", chipClass: "bg-yellow-50 border-yellow-300 text-yellow-700 hover:bg-yellow-100" },
};

function getMeta(type: string) {
  return DISCREPANCY_META[type] ?? {
    label: type,
    badgeClass: "bg-gray-100 text-gray-700 border border-gray-200",
    chipClass: "bg-gray-50 border-gray-300 text-gray-700",
  };
}

// ── Expandable row detail ─────────────────────────────────────────────────────

const CsvRowDetail: React.FC<{ row: NonNullable<Discrepancy["csvRow"]>; rowNum?: number | null; label?: string }> = ({ row, rowNum, label }) => (
  <dl className="space-y-1.5 text-sm">
    {label && <div className="text-xs font-semibold text-blue-600 mb-1">{label}</div>}
    <div className="flex justify-between"><dt className="text-gray-500">Code</dt><dd className="font-mono text-gray-800 text-right break-all">{row.code}</dd></div>
    <div className="flex justify-between"><dt className="text-gray-500">Credits</dt><dd className="font-medium text-gray-800">{row.credits}</dd></div>
    <div className="flex justify-between"><dt className="text-gray-500">Currency</dt><dd className="text-gray-800">{row.devise}</dd></div>
    <div className="flex justify-between"><dt className="text-gray-500">Reference</dt><dd className="font-mono text-gray-800 text-right break-all max-w-[220px]">{row.reference}</dd></div>
    <div className="flex justify-between"><dt className="text-gray-500">Date (UTC)</dt><dd className="text-gray-800">{row.dateDistribution}</dd></div>
    {rowNum != null && <div className="flex justify-between"><dt className="text-gray-500">CSV Row #</dt><dd className="text-gray-800">{rowNum}</dd></div>}
  </dl>
);

const ExpandedDetail: React.FC<{ d: Discrepancy }> = ({ d }) => {
  const isDupProcessor = d.discrepancyType === "DUPLICATE_IN_PROCESSOR";

  return (
    <tr>
      <td colSpan={7} className="px-4 pb-4 bg-gray-50 border-b border-cardborder">
        <div className={`grid grid-cols-1 ${isDupProcessor ? "md:grid-cols-2" : "md:grid-cols-2"} gap-4 pt-3`}>
          {/* CSV side */}
          <div className="rounded-lg border border-cardborder bg-white p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">SELP Data (CSV)</p>
            {isDupProcessor && d.csvRow && d.firstOccurrenceCsvRow ? (
              <div className="space-y-4">
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <CsvRowDetail row={d.csvRow} rowNum={d.csvRowNumber} label="Duplicate occurrence" />
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <CsvRowDetail row={d.firstOccurrenceCsvRow} label="First occurrence" />
                </div>
              </div>
            ) : d.csvRow ? (
              <CsvRowDetail row={d.csvRow} rowNum={d.csvRowNumber} />
            ) : (
              <p className="text-sm text-gray-400 italic">Not present in CSV</p>
            )}
          </div>

          {/* DB side */}
          <div className="rounded-lg border border-cardborder bg-white p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">System Data (DB)</p>
            {d.dbRow ? (
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Saral ID</dt><dd className="text-gray-800">{d.dbRow.saralId ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Code</dt><dd className="font-mono text-gray-800 text-right break-all">{d.dbRow.code ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">State</dt><dd className="text-gray-800">{d.dbRow.state ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Amount</dt><dd className="font-medium text-gray-800">{d.dbRow.amount}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Currency</dt><dd className="text-gray-800">{d.dbRow.currency ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Voucher Ref</dt><dd className="font-mono text-gray-800 text-right break-all max-w-[220px]">{d.dbRow.voucherRef ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Created (UTC)</dt><dd className="text-gray-800">{d.dbRow.createdAtUtc ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Program</dt><dd className="text-gray-800 text-right">{d.dbRow.programName ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Principal</dt><dd className="text-gray-800 text-right">{d.dbRow.principalName ?? "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Distributor</dt><dd className="text-gray-800 text-right">{d.dbRow.distributorName ?? "-"}</dd></div>
              </dl>
            ) : (
              <p className="text-sm text-gray-400 italic">Not found in system</p>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

// ── Discrepancy table row ─────────────────────────────────────────────────────

const DiscrepancyRow: React.FC<{ d: Discrepancy; index: number }> = ({ d, index }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = getMeta(d.discrepancyType);

  return (
    <>
      <tr
        className="border-b border-cardborder hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded((p) => !p)}
      >
        <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
        <td className="px-4 py-3">
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${meta.badgeClass}`}>
            {meta.label}
          </span>
        </td>
        <td className="px-4 py-3 text-xs font-mono text-gray-600" title={d.saralId ?? "-"}>
          {d.saralId ?? "-"}
        </td>
        <td className="px-4 py-3 text-sm font-mono text-gray-700 max-w-[160px] truncate" title={d.voucherCode ?? "-"}>
          {d.voucherCode ?? "-"}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-[140px] truncate" title={d.csvValue ?? "-"}>
          {d.csvValue ?? <span className="text-gray-400">—</span>}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-[140px] truncate" title={d.dbValue ?? "-"}>
          {d.dbValue ?? <span className="text-gray-400">—</span>}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 text-center">
          <span className="flex items-center justify-center gap-1">
            {d.csvRowNumber ?? <span className="text-gray-400">—</span>}
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
          </span>
        </td>
      </tr>
      {expanded && <ExpandedDetail d={d} />}
    </>
  );
};

// ── Filter chip ───────────────────────────────────────────────────────────────

const FilterChip: React.FC<{ label: string; active: boolean; onClick: () => void; chipClass: string }> = ({
  label, active, onClick, chipClass,
}) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${chipClass} ${active ? "ring-2 ring-offset-1 ring-current font-semibold" : ""}`}
  >
    {label}
  </button>
);

// ── Stat card ─────────────────────────────────────────────────────────────────

type StatColor = "neutral" | "green" | "red";

const StatCard: React.FC<{ label: string; value: number; sub: string; color: StatColor }> = ({
  label, value, sub, color,
}) => {
  const valueClass =
    color === "green" ? "text-green-600" :
    color === "red"   ? "text-red-600"   :
                        "text-gray-900";
  return (
    <div className="bg-white rounded-xl border border-cardborder p-5 flex flex-col gap-1">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
      <span className={`text-3xl font-bold ${valueClass}`}>{value.toLocaleString()}</span>
      <span className="text-xs text-gray-400 truncate" title={sub}>{sub}</span>
    </div>
  );
};

// ── Panel ─────────────────────────────────────────────────────────────────────

const SelpReconPanel: React.FC = () => {
  const dispatch = useAppDispatch();

  const today = new Date().toISOString().substring(0, 10);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [csvFileError, setCsvFileError] = useState("");
  const [dateError, setDateError] = useState("");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const { loading, error, result } = useAppSelector((state: RootState) => state.recon);
  const { summary } = result ?? {};

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvFile(e.target.files?.[0] ?? null);
    setCsvFileError("");
    dispatch(clearReconResult());
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let valid = true;
    if (!csvFile) { setCsvFileError("Please upload a CSV file."); valid = false; }
    if (fromDate > toDate) { setDateError("From date must be before or equal to To date."); valid = false; }
    if (!valid) return;
    setCsvFileError("");
    setDateError("");
    setActiveFilter(null);
    dispatch(runRecon({ file: csvFile!, processor: "SELP", fromDate, toDate }));
  };

  const filteredDiscrepancies = result
    ? activeFilter
      ? result.discrepancies.filter((d) => d.discrepancyType === activeFilter)
      : result.discrepancies
    : [];

  return (
    <>
      {/* Upload form */}
      <div className="bg-white rounded-xl p-6 border border-cardborder">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Date range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => { setFromDate(e.target.value); setDateError(""); dispatch(clearReconResult()); }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => { setToDate(e.target.value); setDateError(""); dispatch(clearReconResult()); }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>
          </div>
          {dateError && (
            <div className="flex items-center gap-1 text-red-500 text-sm">
              <AlertCircle className="w-4 h-4" />{dateError}
            </div>
          )}

          {/* File upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SELP Export CSV</label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
            />
            {csvFileError && (
              <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />{csvFileError}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Running...</span>
            ) : "Run Reconciliation"}
          </button>
        </form>
      </div>

      {loading && (
        <div className="bg-white rounded-xl p-10 border border-cardborder flex items-center justify-center gap-3 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin text-brand/80" />
          <span className="text-sm">Processing reconciliation...</span>
        </div>
      )}

      {error && (
        <div className="bg-white rounded-xl p-6 border border-red-200 flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {result && summary && (
        <>
          {/* Header row: stat cards + download */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Query range: <span className="font-medium text-gray-700">{summary.dateFrom} → {summary.dateTo}</span>
              {summary.fileDateFrom && (
                <span className="ml-2 text-gray-400">(file dates: {summary.fileDateFrom} → {summary.fileDateTo})</span>
              )}
            </p>
            <button
              onClick={() => downloadReconReport(result)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80"
            >
              <Download className="w-4 h-4" />
              Download Report
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="CSV Records"       value={summary.totalCsvRows ?? 0} sub={`File: ${summary.fileDateFrom ?? "-"} → ${summary.fileDateTo ?? "-"}`} color="neutral" />
            <StatCard label="DB Records"        value={summary.totalDbRows}       sub={`Query: ${summary.dateFrom} → ${summary.dateTo}`}                      color="neutral" />
            <StatCard label="Matched (Clean)"   value={summary.matchedCount}      sub="No discrepancies"                                                        color="green"   />
            <StatCard
              label="Discrepancies Found"
              value={summary.discrepancyCount}
              sub={summary.discrepancyCount === 0 ? "All records matched" : "See breakdown below"}
              color={summary.discrepancyCount === 0 ? "green" : "red"}
            />
          </div>

          {summary.discrepancyCount === 0 && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">All {summary.totalCsvRows} records matched — no discrepancies found.</span>
            </div>
          )}

          {summary.discrepancyCount > 0 && (
            <div className="bg-white rounded-xl border border-cardborder">
              <div className="px-6 py-4 border-b border-cardborder">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Filter by Type</p>
                <div className="flex flex-wrap gap-2">
                  <FilterChip
                    label={`All (${summary.discrepancyCount})`}
                    active={activeFilter === null}
                    onClick={() => setActiveFilter(null)}
                    chipClass="bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                  />
                  {Object.entries(summary.byType).map(([type, count]) => {
                    const meta = getMeta(type);
                    return (
                      <FilterChip
                        key={type}
                        label={`${meta.label} (${count})`}
                        active={activeFilter === type}
                        onClick={() => setActiveFilter((prev) => (prev === type ? null : type))}
                        chipClass={meta.chipClass}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-cardborder bg-gray-50">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Saral ID</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Code</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">CSV Value</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">DB Value</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">CSV Row</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDiscrepancies.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                          No discrepancies for this filter.
                        </td>
                      </tr>
                    ) : (
                      filteredDiscrepancies.map((d, i) => (
                        <DiscrepancyRow
                          key={`${d.discrepancyType}-${d.voucherCode}-${d.csvRowNumber}-${i}`}
                          d={d}
                          index={i}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-3 border-t border-cardborder bg-gray-50 text-xs text-gray-400">
                Showing {filteredDiscrepancies.length} of {summary.discrepancyCount} discrepancies. Click a row to expand details.
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SelpReconPanel;
